import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";

// Watched bitcoin addresses: an alert when BTC leaves one. Incoming dust is
// ignored on the server.
const BtcWatch = () => {
  const [s, setS] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = (d) => {
    setS(d);
    setText((d.addresses || []).map((a) => (a.label ? `${a.label}, ${a.addr}` : a.addr)).join("\n"));
  };

  useEffect(() => {
    axios.get(`${API}/btc-watch`).then((r) => load(r.data)).catch(() => {});
  }, []);

  const call = async (fn, ok) => {
    setBusy(true);
    try {
      const r = await fn();
      if (r?.data?.addresses) load(r.data);
      if (ok) toast.success(typeof ok === "function" ? ok(r.data) : ok);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed${e?.response?.status ? ` (HTTP ${e.response.status})` : ` (${e.message})`}`);
    }
    setBusy(false);
  };

  const save = () => call(() => axios.put(`${API}/btc-watch`, { ...s, addresses_text: text }),
    (d) => `Watching ${d.addresses.length} address${d.addresses.length === 1 ? "" : "es"}`);
  const connect = () => call(async () => {
    await axios.post(`${API}/btc-watch/connect`);
    return axios.get(`${API}/btc-watch`);
  }, "Telegram connected — check the bot chat");
  const test = () => call(async () => {
    const r = await axios.post(`${API}/btc-watch/test`);
    setS((cur) => ({ ...cur, addresses: cur.addresses.map((a) => ({ ...a, balance: r.data.balances[a.addr] ?? a.balance })) }));
    return r;
  }, (d) => `Test sent · ${d.total_btc.toLocaleString(undefined, { maximumFractionDigits: 2 })} BTC on ${d.read} addresses`
    + (d.usd ? ` (~$${(d.usd / 1e9).toFixed(2)}B)` : "") + (d.missed ? ` · ${d.missed} couldn't be read` : ""));

  if (!s) return null;
  const check = (k, label) => (
    <label className="flex items-center gap-2 text-sm text-gray-200">
      <input type="checkbox" checked={!!s[k]} onChange={(e) => setS({ ...s, [k]: e.target.checked })} />
      {label}
    </label>
  );
  const total = (s.addresses || []).reduce((t, a) => t + (a.balance || 0), 0) / 1e8;

  return (
    <div className="bg-[#111113] border border-[#27272A] rounded-2xl p-4 sm:p-6 space-y-4 mt-4" data-testid="btc-watch">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white">BTC address watch</h2>
        {check("enabled", "Enabled")}
      </div>
      <p className="text-xs text-gray-500">
        Alerts when bitcoin is sent out of one of these addresses — usually within 2 minutes, before it's even
        confirmed. Money coming in is ignored (these addresses get tiny spam deposits all the time).
      </p>

      <div className="space-y-1">
        <textarea
          className="w-full h-40 bg-[#0A0A0A] border border-[#27272A] rounded-md px-3 py-2 text-xs text-white font-mono outline-none focus:border-[#00C2FF]"
          placeholder={"One per line. A name before the address is optional:\nCold wallet 1, bc1q...\nbc1q..."}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <p className="text-[11px] text-gray-500">
          One address per line; anything else on the line becomes its name.
          {s.addresses?.length > 0 && ` ${s.addresses.length} watched · ${total.toLocaleString(undefined, { maximumFractionDigits: 2 })} BTC on them now.`}
        </p>
      </div>

      <div className="border-t border-[#27272A] pt-3 space-y-2">
        <p className="text-sm text-gray-300">Send alerts to:</p>
        <div className="flex items-center gap-4 flex-wrap">
          {check("to_me", "Me on Telegram")}
          {check("to_channel", "Trade channel")}
          {check("to_whatsapp", "My WhatsApp")}
        </div>
        {s.to_me && (
          <div className="flex items-center gap-3 flex-wrap text-xs">
            <span className={s.tg_connected ? "text-[#00E054]" : "text-amber-400"}>
              {s.tg_connected ? "✓ Telegram connected" : "Telegram not connected yet"}
            </span>
            {!s.tg_connected && s.bot && (
              <span className="text-gray-400">
                Open <a href={`https://t.me/${s.bot}`} target="_blank" rel="noopener noreferrer" className="text-[#00C2FF] hover:underline">t.me/{s.bot}</a>, press Start, then:
              </span>
            )}
            <button onClick={connect} disabled={busy} className="px-3 py-1.5 rounded-md border border-[#27272A] text-gray-200 disabled:opacity-50">
              {s.tg_connected ? "Reconnect" : "Connect my Telegram"}
            </button>
          </div>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        <button onClick={save} disabled={busy} className="px-4 py-2 rounded-md bg-[#00C2FF] text-black text-sm font-bold disabled:opacity-50">
          Save address watch
        </button>
        <button onClick={test} disabled={busy} className="px-3 py-2 rounded-md border border-[#27272A] text-gray-200 text-sm">
          Send test alert
        </button>
      </div>
    </div>
  );
};

export default BtcWatch;
